<template> 
  <div class="fts-event-card" @click="(evt) => open(evt)">
    <div class="fts-flex fts-items-start">
      <div class="event-card-wrapper__l">
        <div class="event-type">
          <!-- Studio schedule (docs/scheduling-routine-automation-proposal.md §2) -->
          <span v-if="eventType === 'schedule'" data-schedule-card>On a schedule · {{ scheduleSentence }}</span>
          <span v-else-if="eventType === 'vector'">When module is changed</span>
          <span v-else-if="eventType === 'informationConsole'">When information is changed</span>
          <span v-else-if="eventType === 'activityConsole'">When activity is performed</span>
          <span v-else-if="eventType === 'standardConsole'">When standard activity is performed</span>
          <span v-else-if="eventType === 'stateConsole'">When state is changed</span>

          <template v-else-if="eventType === 'standardActivity'">
            <span>When performing</span>
            <span class="fts-inline-spacer-5"></span>
            <fts-label-group :item="eventItemInfo" />
          </template>

          <template v-else-if="eventType === 'activity'">
            <span>When performing</span>
            <span class="fts-inline-spacer-5"></span>
            <fts-label-group :item="eventItemInfo" emoji :emoji-size="14" />
          </template>

          <template v-else-if="eventType === 'information'">
            <span>When</span>
            <span class="fts-inline-spacer-5"></span>
            <fts-label-group :item="eventItemInfo" information />
            <span class="fts-inline-spacer-5"></span>
            <span>changed</span>
          </template>

          <template v-else-if="eventType === 'state'">
            <span>When state has</span>
            <span class="fts-inline-spacer-5"></span>
          </template>

          <template v-else-if="eventType === 'due'">
            <span>When</span>
            <span class="fts-inline-spacer-5"></span>
            <fts-label-group :item="eventItemInfo" information />
            <span class="fts-inline-spacer-5"></span>
          </template>

          <template v-if="eventType === 'state' || eventType === 'due'">
            <span>{{eventTriggerName}}</span>
            <span class="fts-inline-spacer-5"></span>
          </template>

          <template v-if="eventType === 'state'">
            <fts-state :state="eventItemInfo" />
          </template>
        </div>

        <div class="event-info">
          <font-awesome-icon :icon="eventInfo.icon" class="event-info-icon" v-if="eventInfo.icon" />
          <div class="event-info-label">{{eventInfo.label}}</div>
        </div>
      </div>

      <div class="event-action-container">
        <fts-toggle-switch
          :label="false"
          mat-class
          :size="12"
          v-model="mutableEnabled"
          class="stopPropagation event-state-toggle"
        />

        <fts-tippy content="Delete">
          <fts-btn
            icon
            text
            class="stopPropagation"
            @click="remove"
          >
            <font-awesome-icon :icon="['far', 'trash-alt']" />
          </fts-btn>
        </fts-tippy>
      </div>
    </div>

    <div class="event-recipient-container" v-if="event.recipients && event.recipients.length">
      <div class="event-recipient-list">
        <div class="event-recipient-wrapper" v-for="recipient in event.recipients" :key="recipient.id">
          <!-- member -->
          <fts-identity-display
            identity-type="user"
            :identity-name="recipient.name"
            :identity-username="recipient.identity"
            :profile-photo-size="16"
            v-if="recipient.recipientType === 0"
          />
          <!-- user profile -->
          <fts-identity-display
            :modules="workspaceModules"
            identity-type="profile"
            :identity-id="recipient.id"
            :identity-name="recipient.name"
            :emoji-size="16"
            v-else-if="recipient.recipientType === 7"
          />
          <!-- custom -->
          <fts-identity-display
            identity-type="custom"
            :identity-name="recipient.name"
            v-else-if="recipient.recipientType === 8"
          />
          <!-- information -->
          <fts-identity-display
            :information="informationWithSystem"
            identity-type="information"
            :identity-id="recipient.id"
            :identity-name="recipient.name"
            v-else
          />
        </div>
      </div>
    </div>

    <div class="event-form-container" v-if="eventForm">
      <div class="event-form-list">
        <div v-for="item in eventForm" :key="item.label" class="event-form-wrapper">
          <fts-form-label>{{item.label}}</fts-form-label>
          <div class="event-form-value">{{item.value}}</div>
        </div>
      </div>
    </div>

    <!-- converted by an administrator: it keeps running until switched off; its schedule then takes over -->
    <div class="event-coverage-note" v-if="replacingSchedule" data-replaced-by-schedule>
      <template v-if="replacingSchedule.enabled !== false">
        Converted to the schedule “{{ replacingSchedule.name || "Schedule" }}”, which takes over when this is switched off.
      </template>
      <template v-else>
        Converted to the schedule “{{ replacingSchedule.name || "Schedule" }}”, which is switched off.
      </template>
    </div>
    <!-- a converted schedule waits while the automation it replaces is still on -->
    <div class="event-coverage-note" v-else-if="waitingOriginal" data-waiting-for-original>
      Waits until “{{ waitingOriginalLabel }}” is switched off, then takes over.
    </div>
    <!-- timed automations only reach entries saved after they were set up -->
    <div class="event-coverage-note" v-else-if="legacyTimingNote" data-legacy-timing-note>
      Reaches entries saved after it was set up. For every matching entry, use a Schedule.
    </div>

    <div class="event-badge-container" v-if="eventBadges">
      <div class="event-badge-list">
        <fts-badge
          v-for="badge in eventBadges"
          :key="badge.id"
          variant="pill"
          size="sm"
        >
          {{badge.name}}
        </fts-badge>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import { store as canvasDesignStore } from '../../store/canvasDesignStore';
import { authorizationStore } from '../../store/authorizationStore';
import commons from '../../commons/commons';
import event from './event';
import { conditionCount, describeSchedule, isDigest, isSchedule, originalOf, replacementOf, waitsForOriginal } from '../schedule/scheduleLogic';

import FTSBadge from '../../elements/FTSBadge';
import FTSBtn from '../../elements/FTSBtn';
import FTSFormLabel from '../../elements/FTSFormLabel';
import FTSIdentityDisplay from '../../elements/FTSIdentityDisplay';
import FTSLabelGroup from '../../elements/FTSLabelGroup';
import FTSState from '../../elements/FTSState';
import FTSTippy from '../../elements/FTSTippy';
import FTSToggleSwitch from '../../elements/FTSToggleSwitch';

export default {
  components: {
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-form-label": FTSFormLabel,
    "fts-label-group": FTSLabelGroup,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-identity-display": FTSIdentityDisplay
  },
  props: {
    event: Object
  },
  methods: {
    open(event) {
      if (commons.elementStopPropagation(event))
        return;

      this.$emit('open', this.event);
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Delete automation?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              canvasDesignStore.dispatch("removeVectorTrigger", this.event.id);
            }
          }
        ]
      });
    }
  },
  computed: {
    workspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    informationWithSystem() {
      const system = [
        {
          id: "#createdBy",
          type: 20,
          displayName: "Created By",
          system: true
        },
        {
          id: "#updatedBy",
          type: 20,
          displayName: "Last Change By",
          system: true
        },
        {
          id: "#assignees",
          type: 6,
          displayName: "Assignees",
          system: true
        }
      ];
      return Array.isArray(canvasDesignStore.getters.canvasInformation) ?
        [...this.$store.state.canvas.information, ...system] :
        system;
    },
    eventInfo() {
      return event.getEventInfo(this.event);
    },
    scheduleSentence() {
      return describeSchedule(this.event.schedule);
    },
    /* a timing automation an administrator converted: its replacement schedule, still in the module */
    replacingSchedule() {
      return replacementOf(this.event, canvasDesignStore.getters.vectorTriggers);
    },
    /* a converted schedule: the automation it replaces, while that automation is still on */
    waitingOriginal() {
      const triggers = canvasDesignStore.getters.vectorTriggers;
      return waitsForOriginal(this.event, triggers) ? originalOf(this.event, triggers) : null;
    },
    waitingOriginalLabel() {
      return this.waitingOriginal ? event.getEventInfo(this.waitingOriginal).label : "";
    },
    /* legacy timed automations point at Schedule once schedules are offered (flag appStudioSchedule) */
    legacyTimingNote() {
      return !isSchedule(this.event) &&
        Boolean(authorizationStore.getters.appStudioSchedule) &&
        Boolean(this.event.timeModifier || this.event.trigger === "target");
    },
    eventBadges() {
      let badges = [];
      if (isSchedule(this.event)) {
        const count = conditionCount(this.event.filterSchema);
        badges.push({ id: "mode", name: isDigest(this.event) ? "Digest" : "For each entry" });
        badges.push({ id: "conditions", name: `${count} ${count === 1 ? "condition" : "conditions"}` });
        if (this.event.convertedFrom)
          badges.push({ id: "converted", name: "Converted" });
        return badges;
      }
      if (Boolean(this.event.automationBefore))
        badges.push({ name: "Do before" });
      if (this.event.condition)
        badges.push({ name: "Condition" });
      if (this.event.timeModifier)
        badges.push({ name: "Timing" });
      if (this.event.repeat)
        badges.push({ name: "Recurring" });
      if (this.event.customizeMessage)
        badges.push({ name: "Customize Message" });

      return Boolean(badges.length) && badges;
    },
    eventForm() {
      let form = [];
      if (this.event.timeModifier) {
        if (this.event.targetTime)
          form.push({ label: "Target Time", value: moment(this.event.targetTime).format('hh:mm A') });
        if (this.event.multipler)
          form.push({ label: "Multiplier", value: this.event.multipler });
        if (this.event.durationType)
          form.push({ label: "Duration Type", value: this.event.durationType });
      }

      return Boolean(form.length) && form;
    },
    eventType() {
      return this.event.type === "information" && this.eventItemInfo ?
        event.getEventType(this.event, { informationType: this.eventItemInfo.type }) :
        event.getEventType(this.event);
    },
    eventItemInfo() {
      if (this.event.item && this.event.type)
        switch(this.event.type) {
          case "standardActivity":
            return canvasDesignStore.getters.canvasStandardActivities.find(x => x.id === this.event.item);
          case "activity":
            return canvasDesignStore.getters.canvasActivities.find(x => x.id === this.event.item);
          case "state":
            return canvasDesignStore.getters.canvasStates.find(x => x.id === this.event.item);
          case "information":
            return canvasDesignStore.getters.canvasInformation.find(x => x.id === this.event.item);
        }
    },
    eventTriggerName() {
      const eventTrigger = event.Triggers.find(x => x.id === this.event.trigger);
      return eventTrigger.name;
    },
    mutableEnabled: {
      get() {
        return this.event.enabled === null || this.event.enabled === undefined ?
          true : Boolean(this.event.enabled);
      },
      set(val) {
        let formData = _.cloneDeep(this.event);
        this.$set(formData, "enabled", val);
        canvasDesignStore.dispatch("setVectorTrigger", formData);
      }
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-event-card {
  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
  padding: 0.75rem;
  box-shadow: var(--fts-elevation-2);
  border-radius: $comfort-border-radius;
  cursor: pointer;
  .event-card-wrapper__l {
    min-width: 0;
    flex: 1;
  }
  .event-state-toggle {
    height: 1.25rem;
  }
  .event-type {
    display: flex;
    align-items: center;
    font-size: $font-size;
    line-height: $line-height;
    > span {
      white-space: nowrap;
    }
    /* a schedule's sentence wraps rather than running off the card */
    > span[data-schedule-card] {
      white-space: normal;
    }
  }
  .event-coverage-note {
    margin-top: 0.5rem;
    color: var(--fts-default);
    font-size: 0.75rem;
  }
  .event-info {
    display: flex;
    align-items: center;
    margin-top: 0.5rem;
    font-size: $font-size;
    line-height: $line-height;
  }
  .event-info-icon {
    margin-right: 0.375rem;
    color: var(--fts-default);
  }
  .event-info-label {
    word-break: break-all;
    color: var(--fts-text);
  }
  .event-action-container {
    display: flex;
    align-items: center;
    margin-top: -0.5rem;
    margin-left: 1rem;
    & > * :not(:last-child) {
      margin-left: 0.25rem;
    }
  }
  .event-badge-container {
    display: flex;
    margin-top: 0.75rem;
  }
  .event-badge-list {
    @include flex-gap($display: flex, $gap: 0.25rem);
  }
  .event-form-container {
    display: flex;
    margin-top: 0.75rem;
  }
  .event-form-list {
    @include flex-gap($display: flex, $gapTop: 0.5rem, $gapLeft: 0.75rem);
  }
  .event-form-wrapper {
    display: flex;
    align-items: center;
    font-size: $font-size;
    line-height: $line-height;
  }
  .event-form-value {
    margin-left: 0.375rem;
    color: var(--fts-text);
  }
  .event-recipient-container {
    display: flex;
    margin-top: 0.75rem;
  }
  .event-recipient-list {
    @include flex-gap($display: flex, $gap: 0.5rem);
  }
  .event-recipient-wrapper {
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
  }
}
</style>